import { useEffect, useState } from "react";
import { X, Shield, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { STAT_LABEL } from "@/lib/format";
import { marketCheck, likelyDefender, posGroup } from "@/components/arena/arenaData";
import "./MarketCheckCard.css";

const LABELS = { ...STAT_LABEL, min: "MINUTES", pts: "POINTS", reb: "REBOUNDS", ast: "ASSISTS", fg3m: "3-POINTERS", pra: "PTS+REB+AST" };
const DVP_STATS = ["pts", "reb", "ast", "fg3m", "pra"];
const DVP_WORD = { pts: "πόντους", reb: "ριμπάουντ", ast: "ασίστ", fg3m: "τρίποντα", pra: "PRA" };
const DVP_SHORT = { pts: "PTS", reb: "REB", ast: "AST", fg3m: "3PM", pra: "PRA" };
const GROUP_GR = { G: "γκαρντ", F: "φόργουορντ", C: "σέντερ" };
const rankClass = (d) => (d.rank <= d.of / 3 ? "tough" : d.rank > (d.of * 2) / 3 ? "soft" : "mid");

// Cache the defense-vs-position table (all markets) across card opens.
let dvpCache = null;

export function MarketCheckCard({ row, stat, pick, rows, onClose, onSelectPlayer }) {
  const [dvp, setDvp] = useState(() => dvpCache);
  const [h2h, setH2h] = useState(null);
  const defender = row ? likelyDefender(row, rows) : null;
  const defenderId = defender?.player_id;

  useEffect(() => {
    if (dvpCache) { setDvp(dvpCache); return undefined; }
    let alive = true;
    api.get("/analysis/defense-vs-position")
      .then((r) => { dvpCache = r.data; if (alive) setDvp(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setH2h(null);
    if (!row || !defenderId) return undefined;
    let alive = true;
    api.get("/analysis/head-to-head", { params: { player_id: row.player_id, defender_id: defenderId, stat } })
      .then((r) => alive && setH2h(r.data))
      .catch(() => {});
    return () => { alive = false; };
  }, [row?.player_id, defenderId, stat]);

  if (!row) return null;
  const m = marketCheck(row, stat, pick ? { line: pick.line, side: pick.side, p: pick.p } : {});
  const group = posGroup(row.position);
  const def = dvp && group && dvp.rows ? dvp.rows[`${row.opp}|${group}|${stat}`] : null;
  const defAll = dvp && group && dvp.rows ? DVP_STATS.map((s) => ({ s, d: dvp.rows[`${row.opp}|${group}|${s}`] })).filter((x) => x.d) : [];

  if (!m) {
    return (
      <div className="mcc" data-testid="market-check-card">
        <div className="mcc-top"><span className="mcc-tag">RotoBot check</span>
          <button type="button" className="mcc-close" onClick={onClose} data-testid="mcc-close"><X size={16} /></button></div>
        <p className="mcc-empty">Δεν υπάρχουν αρκετά παιχνίδια.</p>
      </div>
    );
  }

  const marketLine = m.hasMarket ? m.line : null;
  const peak = Math.max(m.line, ...m.last10.map((g) => g.value), 1);
  const up = m.direction === "HIGHER";
  const edgePct = pick && pick.ev != null ? Math.round(pick.ev * 1000) / 10 : null;
  const isValue = pick && pick.is_value !== false && edgePct != null && edgePct > 0;
  const favorable = m.hasMarket ? (edgePct != null && edgePct > 0) : (m.chance != null && m.chance >= 50);
  const h2hHits = h2h && h2h.games ? h2h.games.filter((g) => (m.side === "under" ? g.value < m.line : g.value >= m.line)).length : 0;
  const underLean = def && def.rank <= Math.ceil(def.of / 3);
  const overLean = def && def.rank > def.of - Math.ceil(def.of / 3);

  return (
    <div className="mcc" data-testid="market-check-card">
      <div className="mcc-top">
        <span className="mcc-tag">RotoBot check</span>
        <button type="button" className="mcc-close" onClick={onClose} data-testid="mcc-close" aria-label="Close"><X size={16} /></button>
      </div>

      <div className="mcc-id">
        {row.photo ? <img src={row.photo} alt="" className="mcc-photo" /> : <span className="mcc-photo mcc-photo-fallback">NBA</span>}
        <div>
          <h2 data-testid="mcc-player">{row.name}</h2>
          <p>{row.team} · {row.position || "—"} · {row.home ? "vs" : "@"} {row.opp}</p>
        </div>
      </div>

      <div className={`mcc-line ${up ? "is-up" : "is-down"}`} data-testid="mcc-line">
        <strong>{up ? "HIGHER" : "LOWER"} {m.line}</strong>
        <small>{LABELS[stat] || stat.toUpperCase()}</small>
      </div>

      {isValue && (
        <div className="mcc-value" data-testid="mcc-value">
          <span className="mcc-value-tag">★ VALUE BET</span>
          <b>{m.side === "under" ? "Under" : "Over"} {pick.line} {DVP_SHORT[stat] || stat.toUpperCase()}</b>
          {pick.odds != null && <i>@ {Number(pick.odds).toFixed(2)}</i>}
          <span className="mcc-value-edge">+{edgePct}%</span>
        </div>
      )}

      {m.hasMarket ? (
        <div className="mcc-market" data-testid="mcc-market">
          <div className="mcc-market-main">
            <span className="mcc-market-tag">{pick.bookmaker || "MARKET"}</span>
            <b>{m.side === "under" ? "Under" : "Over"} {pick.line}</b>
            {pick.odds != null && <i>@ {Number(pick.odds).toFixed(2)}</i>}
          </div>
          {edgePct != null && !isValue && (
            <span className={`mcc-edge ${edgePct >= 0 ? "pos" : "neg"}`} data-testid="mcc-edge">
              Edge {edgePct >= 0 ? "+" : ""}{edgePct}%
            </span>
          )}
        </div>
      ) : (
        <div className="mcc-noline" data-testid="mcc-noline">Πρόβλεψη μοντέλου · καμία γραμμή στοιχηματικής ακόμη</div>
      )}

      {underLean && (
        <div className="mcc-under" data-testid="mcc-under">
          <span className="mcc-under-tag">↓ Κλίση UNDER</span>
          <span className="mcc-under-txt">Σφιχτή άμυνα {row.opp} σε {GROUP_GR[group]} · {def.avg} {DVP_WORD[stat]}/αγ. ({def.rank}ος)</span>
        </div>
      )}

      {overLean && (
        <div className="mcc-over" data-testid="mcc-over">
          <span className="mcc-over-tag">↑ Κλίση OVER</span>
          <span className="mcc-over-txt">Γενναιόδωρη άμυνα {row.opp} σε {GROUP_GR[group]} · {def.avg} {DVP_WORD[stat]}/αγ. ({def.rank}ος)</span>
        </div>
      )}

      <div className="mcc-main">
        <div className="mcc-chance">
          <b data-testid="mcc-chance" className={favorable ? "is-good" : "is-bad"}>{m.chance != null ? `${m.chance}%` : "—"}</b>
          <span>{m.hasMarket ? "RotoBot's chance" : "Hit rate φόρμας"}</span>
        </div>
        <div className="mcc-chart" data-testid="mcc-chart">
          <div className="mcc-chart-title">{up ? "Higher" : "Lower"} σε {m.l10hits} από τα τελευταία {m.last10.length}</div>
          <div className="mcc-bars">
            {marketLine != null && (
              <span className="mcc-dash mcc-dash-market" style={{ bottom: `${(marketLine / peak) * 76 + 14}%` }} data-testid="mcc-line-marker">
                <em className="mcc-dash-label">{marketLine}</em>
              </span>
            )}
            {m.last10.map((g, i) => (
              <div key={`${g.date}-${i}`} className="mcc-bar-col" title={`${g.home ? "vs" : "@"} ${g.opp} · ${g.value}`}>
                <span className={`mcc-bar ${g.hit ? "hit" : "miss"}`} style={{ height: `${Math.max(10, (g.value / peak) * 76)}%` }}>
                  <em className="mcc-bar-val">{g.value}</em>
                </span>
                <i className="mcc-bar-opp">{g.opp}</i>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mcc-tiles">
        <div><b>{m.avg10 ?? "—"}</b><span>Μ.Ο. τελευταίων {m.last10.length}</span></div>
        <div><b>{m.season.hits}/{m.season.games}</b><span>Φέτος</span></div>
        <div><b>{m.vsOpp.games ? `${m.vsOpp.hits}/${m.vsOpp.games}` : "—"}</b><span>vs {m.vsOpp.opp}</span></div>
      </div>

      {defender && (
        (() => {
          const clickable = onSelectPlayer && defender.player_id;
          const Comp = clickable ? "button" : "div";
          return (
            <Comp type={clickable ? "button" : undefined} className={`mcc-guard${clickable ? " is-clickable" : ""}`} data-testid="mcc-guard"
              {...(clickable ? { onClick: () => onSelectPlayer(defender.player_id, stat) } : {})}>
              <Shield size={15} />
              <span>Πιθανόν να τον μαρκάρει</span>
              <b>{defender.name}</b>
              <i>{defender.position}{defender.exact ? "" : " ~"}</i>
              {clickable && <ChevronRight size={15} className="mcc-guard-go" />}
            </Comp>
          );
        })()
      )}

      {defender && h2h && h2h.n > 0 && (
        <div className="mcc-h2h" data-testid="mcc-h2h">
          <span>Με {defender.name} στο παρκέ</span>
          <div className="mcc-h2h-stats">
            <b>{h2h.n} {h2h.n === 1 ? "αγ." : "αγ."}</b>
            <b>Μ.Ο. {h2h.avg ?? "—"}</b>
            <b className={h2hHits * 2 >= h2h.n ? "hit" : "miss"}>{h2hHits}/{h2h.n} {up ? "πάνω" : "κάτω"}</b>
          </div>
        </div>
      )}

      {def && (
        <div className="mcc-dvp" data-testid="mcc-dvp">
          <div className="mcc-dvp-main">
            <span>Άμυνα {row.opp} σε {GROUP_GR[group]}</span>
            <b>{def.avg} <small>{DVP_WORD[stat]}/αγ.</small></b>
          </div>
          <span className={`mcc-dvp-rank ${rankClass(def)}`}>
            {def.rank}ος / {def.of} λιγότερους
          </span>
        </div>
      )}

      {defAll.length > 0 && (
        <div className="mcc-dvp-badges" data-testid="mcc-dvp-badges">
          {defAll.map(({ s, d }) => (
            <span key={s} className={`mcc-dvp-badge ${rankClass(d)}${s === stat ? " is-active" : ""}`} title={`${row.opp} σε ${GROUP_GR[group]}: ${d.avg} ${DVP_WORD[s]}/αγ. · ${d.rank}ος`}>
              <em>{DVP_SHORT[s]}</em>#{d.rank}
            </span>
          ))}
        </div>
      )}

      <div className="mcc-checks">
        <div className={m.l10hits * 2 >= m.last10.length ? "ok" : "no"}>
          <i>{m.l10hits * 2 >= m.last10.length ? "✓" : "✕"}</i>
          <b>{m.l10hits}/{m.last10.length}</b>
          <span>{up ? "Πάνω" : "Κάτω"} από τη γραμμή τις τελευταίες {m.last10.length}</span>
        </div>
        <div className={row.home ? "ok" : "no"}>
          <i>{row.home ? "✓" : "–"}</i>
          <b>{row.home ? "Εντός" : "Εκτός"}</b>
          <span>έδρας απόψε</span>
        </div>
      </div>
    </div>
  );
}
